import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Zap, BarChart3, Mail, Activity, Database, CheckCircle2, type LucideIcon } from "lucide-react";
import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import gsap from "gsap";
import api from "../services/api";
import { AxiosError } from "axios";

// --- GLSL Vertex Shader: Pipeline for Particles ---
const HERO_VERTEX_SHADER = /* glsl */ `
  uniform float uTime;
  uniform vec2 uLeftTip;
  uniform vec2 uAxis;
  uniform vec2 uPerp;
  uniform vec2 uEmitStart;
  uniform vec2 uEmitEnd;
  uniform vec2 uEmitDir;

  attribute float aSide;
  attribute float aT0;
  attribute float aRow;
  attribute float aWavePhase;

  varying float vAlpha;
  varying float vSide;

  void main() {
    vec2 p;
    float t;
    if (aSide < 0.0) {
      // Incoming — wavy curves converging into left tip
      t = fract(uTime * 0.09 + aT0);
      vec2 perpOffset = uPerp * aRow * 1.6;
      vec2 source = uLeftTip - uAxis * 5.5 + perpOffset;
      p = mix(source, uLeftTip, t);
      float damp = pow(1.0 - t, 1.3);
      float wave = sin(t * 7.5 + aWavePhase) * 0.55 * damp;
      p += uPerp * wave;
      vAlpha = smoothstep(0.0, 0.08, t) * (1.0 - smoothstep(0.88, 1.0, t));
    } else {
      // Outgoing — 1:1 replica of the incoming pattern, converging into right tip
      t = fract(uTime * 0.09 + aT0);
      vec2 perpOffset = uPerp * aRow * 1.6;
      vec2 source = uEmitStart + uEmitDir * 5.5 + perpOffset;
      p = mix(source, uEmitStart, t);
      float damp = pow(1.0 - t, 1.3);
      float wave = sin(t * 7.5 + aWavePhase) * 0.55 * damp;
      p += uPerp * wave;
      vAlpha = smoothstep(0.0, 0.08, t) * (1.0 - smoothstep(0.88, 1.0, t));
    }

    vec4 mvPosition = modelViewMatrix * vec4(p, 0.0, 1.0);
    gl_PointSize = 2.2 * (1.0 / -mvPosition.z);
    gl_Position = projectionMatrix * mvPosition;
    vSide = aSide;
  }
`;

const HERO_FRAGMENT_SHADER = /* glsl */ `
  varying float vAlpha;
  varying float vSide;
  void main() {
    float d = distance(gl_PointCoord, vec2(0.5));
    if (d > 0.5) discard;

    vec3 colorIn = vec3(0.35, 0.55, 0.45);
    vec3 colorOut = vec3(0.06, 0.72, 0.5);
    vec3 c = vSide < 0.0 ? colorIn : colorOut;

    float edge = 1.0 - smoothstep(0.35, 0.5, d);
    gl_FragColor = vec4(c, edge * vAlpha);
  }
`;

// --- GLSL Shaders for Background Port Outline ---
const BACKGROUND_VERTEX_SHADER = /* glsl */ `
  uniform float uTime;
  attribute float aRow;
  varying float vAlpha;

  void main() {
    vec3 p = position;
    // Slow drift for background particles
    p.x += sin(uTime * 0.1) * 0.1;
    p.y += cos(uTime * 0.08 + aRow * 10.0) * 0.1;

    vec4 mvPosition = modelViewMatrix * vec4(p, 1.0);
    gl_PointSize = 1.5 * (1.0 / -mvPosition.z);
    gl_Position = projectionMatrix * mvPosition;
    
    // Abstract grid fading effect
    float grid = step(0.1, fract(p.x * 2.0)) * step(0.1, fract(p.y * 2.0));
    vAlpha = 0.05 + 0.02 * grid;
  }
`;

const BACKGROUND_FRAGMENT_SHADER = /* glsl */ `
  varying float vAlpha;
  void main() {
    float d = distance(gl_PointCoord, vec2(0.5));
    if (d > 0.5) discard;
    
    vec3 color = vec3(0.35, 0.5, 0.45); // Neutral grey-green
    float edge = 1.0 - smoothstep(0.35, 0.5, d);
    gl_FragColor = vec4(color, edge * vAlpha);
  }
`;

const PARTICLE_COUNT = 25000;

function canUseWebGLHero(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return false;
  try {
    const c = document.createElement("canvas");
    const gl = c.getContext("webgl2") || c.getContext("webgl");
    return !!gl;
  } catch {
    return false;
  }
}

function HeroFallback({ textRef }: { textRef: React.RefObject<HTMLDivElement | null> }) {
  useEffect(() => {
    if (!textRef.current) return;
    const tween = gsap.to(textRef.current, {
      opacity: 1,
      y: 0,
      duration: 1.5,
      delay: 0.3,
      ease: "expo.out",
    });
    return () => { tween.kill(); };
  }, [textRef]);

  return (
    <div className="absolute inset-0 bg-gradient-to-br from-[#020505] via-[#0A2F2F]/25 to-[#020505]">
      <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 2px 2px, #0A2F2F 1px, transparent 0)", backgroundSize: "40px 40px" }} />
    </div>
  );
}

function HeroCanvas({ paused = false }: { paused?: boolean }) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const textRef = useRef<HTMLDivElement | null>(null);
  const [useCanvas] = useState(canUseWebGLHero);
  const pausedRef = useRef(paused);
  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);

  useEffect(() => {
    if (!useCanvas) return;
    const mount = mountRef.current;
    if (!mount) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, mount.clientWidth / mount.clientHeight, 0.1, 1000);
    camera.position.z = 5;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    // --- Shuttle (3D): extruded compound-curve frame + bobbin shaft + spool ---
    const SHUTTLE_PITCH_X = -0.7;                // ~40° forward pitch — side wall + chamfer visible, not a flat paper
    const SHUTTLE_YAW_Y = -0.4;                  // ~23° yaw — exposes full side face + front end cap
    const SHUTTLE_ROLL_Z = (15 * Math.PI) / 180; // 15° screen roll (head lower-left → tail upper-right)
    const HW = 2.2;    // half total length
    const HH = 0.5;    // half height at middle
    const BHW = HW * 0.4; // flat middle spans [-BHW, BHW]
    const DEPTH = 0.30; // deeper extrusion → thick side wall visible under pitch
    const TIP_CURVE = 0.55; // how far in the tip arc reaches
    const TIP_HH = 0.08; // half-height of the blunt flat tip face

    const shuttleGroup = new THREE.Group();

    // Outer silhouette: curved tips with short flat faces (no sharp points)
    const frameShape = new THREE.Shape();
    frameShape.moveTo(-HW, TIP_HH);
    frameShape.quadraticCurveTo(-HW + TIP_CURVE, HH * 0.9, -BHW, HH);
    frameShape.quadraticCurveTo(0, HH * 1.04, BHW, HH);
    frameShape.quadraticCurveTo(HW - TIP_CURVE, HH * 0.9, HW, TIP_HH);
    frameShape.lineTo(HW, -TIP_HH);
    frameShape.quadraticCurveTo(HW - TIP_CURVE, -HH * 0.9, BHW, -HH);
    frameShape.quadraticCurveTo(0, -HH * 1.04, -BHW, -HH);
    frameShape.quadraticCurveTo(-HW + TIP_CURVE, -HH * 0.9, -HW, -TIP_HH);
    frameShape.lineTo(-HW, TIP_HH);

    // Rounded-rectangle window in the body
    const IHW = HW * 0.62;
    const IHH = HH * 0.58;
    const RC = 0.1;
    const frameHole = new THREE.Path();
    frameHole.moveTo(-IHW + RC, -IHH);
    frameHole.lineTo(IHW - RC, -IHH);
    frameHole.quadraticCurveTo(IHW, -IHH, IHW, -IHH + RC);
    frameHole.lineTo(IHW, IHH - RC);
    frameHole.quadraticCurveTo(IHW, IHH, IHW - RC, IHH);
    frameHole.lineTo(-IHW + RC, IHH);
    frameHole.quadraticCurveTo(-IHW, IHH, -IHW, IHH - RC);
    frameHole.lineTo(-IHW, -IHH + RC);
    frameHole.quadraticCurveTo(-IHW, -IHH, -IHW + RC, -IHH);
    frameShape.holes.push(frameHole);

    // Body outline rendered as particles (top + bottom face silhouettes)
    const OUTLINE_OUTER_SAMPLES = 320;
    const OUTLINE_HOLE_SAMPLES = 160;
    const outerPts = frameShape.getPoints(OUTLINE_OUTER_SAMPLES);
    const holePts = frameHole.getPoints(OUTLINE_HOLE_SAMPLES);
    const topZ = DEPTH / 2;
    const bottomZ = -DEPTH / 2;
    const outlineCount = (outerPts.length + holePts.length) * 2;
    const outlinePos = new Float32Array(outlineCount * 3);
    let op = 0;
    for (const pt of outerPts) {
      outlinePos[op++] = pt.x; outlinePos[op++] = pt.y; outlinePos[op++] = topZ;
    }
    for (const pt of holePts) {
      outlinePos[op++] = pt.x; outlinePos[op++] = pt.y; outlinePos[op++] = topZ;
    }
    for (const pt of outerPts) {
      outlinePos[op++] = pt.x; outlinePos[op++] = pt.y; outlinePos[op++] = bottomZ;
    }
    for (const pt of holePts) {
      outlinePos[op++] = pt.x; outlinePos[op++] = pt.y; outlinePos[op++] = bottomZ;
    }

    const frameGeo = new THREE.BufferGeometry();
    frameGeo.setAttribute("position", new THREE.BufferAttribute(outlinePos, 3));
    const frameMat = new THREE.PointsMaterial({
      color: 0x10b981,
      size: 0.035,
      transparent: true,
      opacity: 0.6,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true,
    });
    const frameMesh = new THREE.Points(frameGeo, frameMat);
    shuttleGroup.add(frameMesh);

    // "EA" letters as particles — only interior content inside the frame
    const textCanvas = document.createElement("canvas");
    const TCW = 520;
    const TCH = 220;
    const TSTRIDE = 5; // denser letter fill
    textCanvas.width = TCW;
    textCanvas.height = TCH;
    const tctx = textCanvas.getContext("2d") as
      | (CanvasRenderingContext2D & { letterSpacing?: string })
      | null;
    const textGeo = new THREE.BufferGeometry();
    const textMat = new THREE.PointsMaterial({
      color: 0xcdf5dc,
      size: 0.022,
      transparent: true,
      opacity: 0.4,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true,
    });
    if (tctx) {
      tctx.fillStyle = "black";
      tctx.fillRect(0, 0, TCW, TCH);
      tctx.fillStyle = "white";
      tctx.font = "900 165px system-ui, Helvetica, Arial, sans-serif";
      if ("letterSpacing" in tctx) tctx.letterSpacing = "56px";
      tctx.textAlign = "center";
      tctx.textBaseline = "middle";
      tctx.fillText("EA", TCW / 2, TCH / 2);
      const pixels = tctx.getImageData(0, 0, TCW, TCH).data;
      const textPts: number[] = [];
      const textWidth = 1.55;
      const textHeight = 0.66;
      const textZ = 0;
      for (let py = 0; py < TCH; py += TSTRIDE) {
        for (let px = 0; px < TCW; px += TSTRIDE) {
          if (pixels[(py * TCW + px) * 4] > 128) {
            textPts.push(
              (px / TCW - 0.5) * textWidth,
              -(py / TCH - 0.5) * textHeight,
              textZ,
            );
          }
        }
      }
      textGeo.setAttribute(
        "position",
        new THREE.BufferAttribute(new Float32Array(textPts), 3),
      );
    }
    const textMesh = new THREE.Points(textGeo, textMat);
    shuttleGroup.add(textMesh);

    shuttleGroup.rotation.x = SHUTTLE_PITCH_X;
    shuttleGroup.rotation.y = SHUTTLE_YAW_Y;
    shuttleGroup.rotation.z = SHUTTLE_ROLL_Z;
    scene.add(shuttleGroup);

    // Lighting (for PBR materials; particle shader is unaffected)
    const ambient = new THREE.AmbientLight(0x2e4238, 0.7);
    scene.add(ambient);
    const keyLight = new THREE.DirectionalLight(0xcdeedb, 1.4);
    keyLight.position.set(3, 4, 5);
    scene.add(keyLight);
    const rimLight = new THREE.DirectionalLight(0x10b981, 0.6);
    rimLight.position.set(-3, -2, 3);
    scene.add(rimLight);

    // World-space geometry anchors (drives particle entry/exit)
    shuttleGroup.updateMatrixWorld();
    const leftTipW = new THREE.Vector3(-HW, 0, 0).applyMatrix4(shuttleGroup.matrixWorld);
    const rightTipW = new THREE.Vector3(HW, 0, 0).applyMatrix4(shuttleGroup.matrixWorld);
    const leftTip = new THREE.Vector2(leftTipW.x, leftTipW.y);
    const rightTip = new THREE.Vector2(rightTipW.x, rightTipW.y);
    const axis = rightTip.clone().sub(leftTip).normalize();
    const perp = new THREE.Vector2(-axis.y, axis.x);

    // Outgoing wavy curves radiate from the right tip — both endpoints
    // collapse to the tip; the perpendicular spread comes from the shader
    const emitStart = rightTip.clone();
    const emitEnd = rightTip.clone();
    const emitDir = axis.clone();

    // --- Particle buffer: half incoming, half outgoing ---
    const geo = new THREE.BufferGeometry();
    const positions = new Float32Array(PARTICLE_COUNT * 3);
    const sides = new Float32Array(PARTICLE_COUNT);
    const t0s = new Float32Array(PARTICLE_COUNT);
    const rows = new Float32Array(PARTICLE_COUNT);
    const wavePhases = new Float32Array(PARTICLE_COUNT);

    const half = PARTICLE_COUNT / 2;
    const INCOMING_LINES = 30;
    const OUTGOING_LINES = 30;

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      if (i < half) {
        sides[i] = -1;
        const line = i % INCOMING_LINES;
        rows[i] = (line / (INCOMING_LINES - 1) - 0.5) * 2;
        wavePhases[i] = line * 0.41;
      } else {
        sides[i] = 1;
        const line = (i - half) % OUTGOING_LINES;
        rows[i] = (line / (OUTGOING_LINES - 1) - 0.5) * 2;
        wavePhases[i] = line * 0.41;
      }
      t0s[i] = Math.random();
    }

    geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geo.setAttribute("aSide", new THREE.BufferAttribute(sides, 1));
    geo.setAttribute("aT0", new THREE.BufferAttribute(t0s, 1));
    geo.setAttribute("aRow", new THREE.BufferAttribute(rows, 1));
    geo.setAttribute("aWavePhase", new THREE.BufferAttribute(wavePhases, 1));
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 15);

    const mat = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uLeftTip: { value: leftTip },
        uAxis: { value: axis },
        uPerp: { value: perp },
        uEmitStart: { value: emitStart },
        uEmitEnd: { value: emitEnd },
        uEmitDir: { value: emitDir },
      },
      vertexShader: HERO_VERTEX_SHADER,
      fragmentShader: HERO_FRAGMENT_SHADER,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    const particles = new THREE.Points(geo, mat);
    scene.add(particles);

    // --- (New) Background Port Outline with Particles & Dotted Lines ---
    const BG_PARTICLE_COUNT = 8000;
    const bgGeo = new THREE.BufferGeometry();
    const bgPos = new Float32Array(BG_PARTICLE_COUNT * 3);
    const bgRows = new Float32Array(BG_PARTICLE_COUNT);

    // Very abstract port outline hinted by dotted grid lines
    const GRID_SIZE = 10;
    for (let i = 0; i < BG_PARTICLE_COUNT; i++) {
      // Abstract harbor/crane points
      let x = (Math.random() - 0.5) * 20;
      let y = (Math.random() - 0.5) * 12;
      const z = -2 + (Math.random() - 0.5) * 0.5; // deeper than main cloud

      // Align some to a grid to create ghost lines/crane forms
      const gridU = i % GRID_SIZE;
      const gridV = Math.floor((i / GRID_SIZE) % GRID_SIZE);
      const isGhostLine = i < 3000 && Math.random() < 0.6;
      
      if (isGhostLine) {
         if (i < 1500) { // vertical pillars/cranes
            x = -7.5 + gridU * 1.5 + Math.random() * 0.05;
            y = -4.0 + (i / 1500) * 8.0;
         } else { // horizontal gantry beams
            x = -8.0 + (i - 1500) / 1500 * 16.0;
            y = -1.0 + gridV * 0.8;
         }
      }

      bgPos[i * 3] = x;
      bgPos[i * 3 + 1] = y;
      bgPos[i * 3 + 2] = z;
      bgRows[i] = Math.random();
    }
    bgGeo.setAttribute("position", new THREE.BufferAttribute(bgPos, 3));
    bgGeo.setAttribute("aRow", new THREE.BufferAttribute(bgRows, 1));

    const bgMat = new THREE.ShaderMaterial({
      uniforms: { uTime: { value: 0 } },
      vertexShader: BACKGROUND_VERTEX_SHADER,
      fragmentShader: BACKGROUND_FRAGMENT_SHADER,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const bgParticles = new THREE.Points(bgGeo, bgMat);
    scene.add(bgParticles);

    const renderScene = new RenderPass(scene, camera);
    const bloomPass = new UnrealBloomPass(new THREE.Vector2(mount.clientWidth, mount.clientHeight), 1.5, 0.4, 0.85);
    bloomPass.threshold = 0.2;
    bloomPass.strength = 2.0;
    bloomPass.radius = 0.5;

    const composer = new EffectComposer(renderer);
    composer.addPass(renderScene);
    composer.addPass(bloomPass);

    const textTween = textRef.current ? gsap.to(textRef.current, { opacity: 1, y: 0, duration: 2, delay: 1, ease: "expo.out" }) : null;

    let rafId = 0;
    let running = false;
    const tick = () => {
      if (!pausedRef.current) {
        mat.uniforms.uTime.value += 0.01;
        bgMat.uniforms.uTime.value += 0.01;
        composer.render();
      }
      rafId = requestAnimationFrame(tick);
    };
    const start = () => { if (running) return; running = true; rafId = requestAnimationFrame(tick); };
    const stop = () => { if (!running) return; running = false; cancelAnimationFrame(rafId); };

    const io = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) start(); else stop(); }, { threshold: 0 });
    io.observe(mount);
    start();

    const handleResize = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      camera.aspect = w / h; camera.updateProjectionMatrix();
      renderer.setSize(w, h); composer.setSize(w, h);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      stop(); io.disconnect();
      window.removeEventListener("resize", handleResize);
      textTween?.kill();
      geo.dispose(); mat.dispose();
      frameGeo.dispose(); frameMat.dispose();
      textGeo.dispose(); textMat.dispose();
      bgGeo.dispose(); bgMat.dispose();
      composer.dispose(); renderer.dispose();
      if (renderer.domElement.parentNode === mount) { mount.removeChild(renderer.domElement); }
    };
  }, [useCanvas]);

  return (
    <>
      {useCanvas ? <div ref={mountRef} className="absolute inset-0" /> : <HeroFallback textRef={textRef} />}
      <div ref={textRef} className="absolute top-20 md:top-24 left-0 right-0 z-10 text-center px-6 opacity-0 translate-y-8">
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-black uppercase leading-[0.9] tracking-[-0.02em] m-0">
          Untangle the chaos.<br /><span className="text-[#10B981] [text-shadow:0_0_20px_rgba(16,185,129,0.4)]">Weave the order.</span>
        </h1>
      </div>
    </>
  );
}

const PIPELINE_STEPS: {
  icon: LucideIcon;
  title: string;
  desc: ReactNode;
  badge: string;
  variant?: "wide" | "showcase";
}[] = [
  {
    icon: Mail,
    title: "Automated Email Ingestion",
    desc: (
      <>
        <span className="text-emerald-400 font-semibold">Intelligent recognition</span>{" "}
        auto-parses order data — no more manual entry.
      </>
    ),
    badge: "Auto-Ingest",
    variant: "wide",
  },
  {
    icon: Shield,
    title: "Live Demurrage Alerts",
    desc: "Precise alerts seal profit leaks.",
    badge: "Fee-Shield",
  },
  {
    icon: Zap,
    title: "Lightning Dispatch",
    desc: "Seconds to assign — end the phone chaos.",
    badge: "Smart Dispatch",
  },
  {
    icon: BarChart3,
    title: "End-to-End Asset Visibility",
    desc: "Digital ops hub — reclaim 4 hours, daily.",
    badge: "Asset Sync",
  },
  {
    icon: CheckCircle2,
    title: "Instant Billing Automation",
    desc: "“Zero-error reconciliation — profit settles itself.”",
    badge: "Pro-Billing",
    variant: "showcase",
  },
];

const Home = () => {
  const navigate = useNavigate();
  const heroBottomRef = useRef<HTMLDivElement | null>(null);
  const [isDemoOpen, setIsDemoOpen] = useState(false);
  const [demoSubmitted, setDemoSubmitted] = useState(false);
  const [demoSubmitting, setDemoSubmitting] = useState(false);
  const [demoError, setDemoError] = useState<string | null>(null);

  const closeDemo = () => {
    setIsDemoOpen(false);
    setDemoSubmitted(false);
    setDemoError(null);
    setDemoSubmitting(false);
  };

  const handleDemoSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (demoSubmitting) return;

    const form = e.currentTarget;
    const formData = new FormData(form);
    const name = String(formData.get("name") || "").trim();
    const email = String(formData.get("email") || "").trim();

    if (!name || !email) {
      setDemoError("Please enter your name and email.");
      return;
    }

    setDemoError(null);
    setDemoSubmitting(true);
    try {
      await api.post("/public/demo-request", { name, email });
      setDemoSubmitted(true);
    } catch (err) {
      const axiosErr = err as AxiosError<{ message?: string }>;
      const serverMsg = axiosErr.response?.data?.message;
      setDemoError(
        serverMsg ||
          "Could not submit your request. Please check your connection and try again."
      );
    } finally {
      setDemoSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isDemoOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") closeDemo(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isDemoOpen]);

  useEffect(() => {
    if (!heroBottomRef.current) return;
    const tween = gsap.to(heroBottomRef.current, {
      opacity: 1,
      y: 0,
      duration: 2,
      delay: 1,
      ease: "expo.out",
    });
    return () => {
      tween.kill();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#05070a] text-slate-200 font-sans selection:bg-[#0A2F2F] selection:text-white">
      {/* --- Sticky Nav --- */}
      <nav className="fixed top-0 w-full z-50 border-b border-white/5 bg-[#050A0A]/80 backdrop-blur-md py-4">
        <div
          className="max-w-6xl mx-auto px-6 grid items-center"
          style={{ gridTemplateColumns: "1fr auto 1fr" }}
        >
          <div className="text-xl font-bold tracking-tighter text-white justify-self-start">
            <span className="text-white">[ </span>
            EA<span className="text-[#10B981]">loom</span>
            <span className="text-white"> ]</span>
          </div>
          <div className="hidden md:flex gap-8 text-sm font-medium uppercase tracking-widest text-slate-400 justify-self-center">
            <a href="#efficiency" className="hover:text-white transition-colors">Efficiency</a>
            <a href="#pipeline" className="hover:text-white transition-colors">Pipeline</a>
            <a href="#ai" className="hover:text-white transition-colors">AI Vision</a>
            <a href="#demo" className="hover:text-white transition-colors">Demo</a>
          </div>
          <button
            onClick={() => navigate("/login")}
            className="text-xl font-bold tracking-tighter text-white hover:opacity-80 transition-opacity justify-self-end"
          >
            [ <span className="text-[#10B981]">Login</span> ]
          </button>
        </div>
      </nav>

      {/* --- Section 1: Hero (The Loom) --- */}
      <section className="relative h-screen w-full overflow-hidden">
        <HeroCanvas paused={isDemoOpen} />
        <div
          ref={heroBottomRef}
          className="absolute bottom-12 md:bottom-20 left-0 right-0 z-10 flex flex-col items-center gap-6 px-6 opacity-0 translate-y-8"
        >
          <button onClick={() => setIsDemoOpen(true)} className="px-10 py-4 bg-[#0A2F2F] border border-[#10B981]/30 text-white text-xs md:text-sm font-bold tracking-[0.25em] uppercase transition-all hover:bg-[#10B981] hover:shadow-[0_0_30px_rgba(16,185,129,0.5)]">
            [ Begin Your Digital Operations ]
          </button>
        </div>
      </section>

      {/* --- Section 2: ROI Dashboard --- */}
      <section id="efficiency" className="py-16 px-6 antialiased">
        <div className="max-w-6xl mx-auto">
          {/* Section header */}
          <div className="mb-12 border-l-2 border-emerald-500 pl-6">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
              EAloom{" "}
              <span className="bg-gradient-to-r from-emerald-400 to-cyan-500 bg-clip-text text-transparent">
                Quantified Cost &amp; Efficiency Model
              </span>
            </h2>
            <p className="text-slate-500 text-xs tracking-[0.2em] uppercase font-mono">
              Based on 2026 industry-average fleet data
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-5 items-stretch">
            {/* Card 1: 50% + progress bars — col-span-7 */}
            <div className="md:col-span-7 bg-[#0d0d0d] border border-white/10 rounded-3xl p-8 relative overflow-hidden group">
              <div className="relative z-10">
                <div className="flex justify-between items-start mb-8 gap-4">
                  <div>
                    <h3 className="text-7xl md:text-8xl font-black text-white tracking-tighter">50%</h3>
                    <p className="text-lg font-bold text-emerald-400 mt-1 uppercase italic">
                      Cut admin costs in half
                    </p>
                  </div>
                  <div className="text-right text-xs text-slate-500 font-mono leading-snug shrink-0">
                    EST. SAVINGS
                    <br />
                    $45,000+ / YR / DISPATCHER
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-white/5 p-4 rounded-xl border border-white/5">
                    <div className="flex justify-between text-xs mb-2">
                      <span className="text-slate-400">Order Intake Automation (Email Ingest)</span>
                      <span className="text-emerald-400 font-bold">−80% time</span>
                    </div>
                    <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.5)] w-[80%]" />
                    </div>
                  </div>
                  <div className="bg-white/5 p-4 rounded-xl border border-white/5">
                    <div className="flex justify-between text-xs mb-2">
                      <span className="text-slate-400">Financial Reconciliation (Auto-Billing)</span>
                      <span className="text-emerald-400 font-bold">Zero error · seconds</span>
                    </div>
                    <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.5)] w-[95%]" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 2: 400% Scale — col-span-5 */}
            <div className="md:col-span-5 bg-[#0d0d0d] border border-white/10 rounded-3xl p-8 flex flex-col justify-between hover:border-emerald-500/20 transition-all">
              <div>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-6">
                  Productivity Uplift (Scale)
                </p>
                <div className="flex items-baseline gap-2">
                  <span className="text-5xl font-black text-white tracking-tighter">400%</span>
                  <span className="text-emerald-500 text-sm font-bold">▲ 4×</span>
                </div>
                <p className="text-slate-400 text-sm mt-4 leading-relaxed">
                  From 1 dispatcher managing 25 trucks to{" "}
                  <span className="text-white font-bold underline decoration-emerald-500">
                    1 dispatcher running 100
                  </span>{" "}
                  — no extra hires.
                </p>
              </div>
              <div className="mt-8 flex gap-1">
                <div className="w-1.5 h-6 bg-emerald-500" />
                <div className="w-1.5 h-6 bg-emerald-500" />
                <div className="w-1.5 h-6 bg-emerald-500" />
                <div className="w-1.5 h-6 bg-emerald-500" />
                <div className="w-1.5 h-6 bg-white/10" />
              </div>
            </div>

            {/* Time breakdown card — col-span-12 */}
            <div className="md:col-span-12 bg-gradient-to-r from-[#0d0d0d] to-[#111] border border-white/10 rounded-3xl p-8">
              <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
                <div className="shrink-0">
                  <div className="text-5xl font-black text-white">4.5 Hours</div>
                  <p className="text-emerald-500 text-xs font-bold uppercase tracking-widest mt-1">
                    Daily time reclaimed per person
                  </p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-8 flex-grow">
                  <div>
                    <p className="text-xs text-slate-500 uppercase font-bold mb-2 italic tracking-wider">Email Intake</p>
                    <p className="text-base text-slate-300 font-medium">
                      Saves <span className="text-emerald-400 font-bold">1.5h</span>
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase font-bold mb-2 italic tracking-wider">Phone Dispatch</p>
                    <p className="text-base text-slate-300 font-medium">
                      Saves <span className="text-emerald-400 font-bold">2.0h</span>
                    </p>
                  </div>
                  <div className="col-span-2 md:col-span-1">
                    <p className="text-xs text-slate-500 uppercase font-bold mb-2 italic tracking-wider">Demurrage Watch</p>
                    <p className="text-base text-slate-300 font-medium">
                      Eliminates <span className="text-emerald-400 font-bold">100%</span> demurrage risk
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom CTA — emerald-framed, col-span-12 */}
            <div className="md:col-span-12 bg-emerald-500 rounded-[2rem] p-[1.5px] mt-4 shadow-[0_20px_50px_-20px_rgba(16,185,129,0.3)]">
              <div className="bg-black rounded-[2rem] px-8 md:px-12 py-10 md:py-12 flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-6 md:gap-8">
                  <div className="text-8xl font-black text-emerald-400 tracking-tighter">25%</div>
                  <div>
                    <p className="text-lg font-bold text-emerald-400 leading-tight">
                      Core business time gained
                    </p>
                    <p className="text-slate-500 text-[10px] font-mono mt-1">
                      Based on 4.5h of an 18h daily operating window
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsDemoOpen(true)}
                  className="bg-emerald-500 hover:bg-emerald-400 text-black font-black px-10 md:px-12 py-4 rounded-xl transition-all active:scale-95 text-sm uppercase whitespace-nowrap"
                >
                  Get Your Custom ROI Report
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- Section 3: The Pipeline --- */}
      {/* --- Section 3: Pipeline --- */}
      <section id="pipeline" className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
              EAloom Pipeline —{" "}
              <span className="bg-gradient-to-r from-emerald-400 to-cyan-500 bg-clip-text text-transparent">
                Digital Operations Flow
              </span>
            </h2>
            <p className="text-slate-400 text-lg">
              From email ingest to billing close — every step digitally accelerated.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 md:grid-rows-2 gap-4 md:h-[600px]">
            {PIPELINE_STEPS.map((s, i) => {
              const Icon = s.icon;
              const isWide = s.variant === "wide";
              const isShowcase = s.variant === "showcase";
              return (
                <div
                  key={i}
                  className={`group relative p-8 rounded-3xl overflow-hidden transition-all duration-500 ${
                    isWide ? "md:col-span-2 md:row-span-1" : "md:col-span-1 md:row-span-1"
                  } ${
                    isShowcase
                      ? "bg-gradient-to-br from-[#1a1a1a] to-[#111] border border-emerald-500/30 shadow-[0_0_30px_-10px_rgba(16,185,129,0.2)]"
                      : "bg-[#111] border border-white/10 hover:border-emerald-500/50"
                  }`}
                >
                  <div className="relative z-10">
                    <div
                      className={`w-12 h-12 rounded-lg flex items-center justify-center mb-6 ${
                        isShowcase
                          ? "bg-emerald-500"
                          : "bg-emerald-500/10 border border-emerald-500/20"
                      }`}
                    >
                      <Icon className={`w-6 h-6 ${isShowcase ? "text-black" : "text-emerald-400"}`} strokeWidth={isShowcase ? 2 : 1.5} />
                    </div>
                    <h3 className={`font-bold mb-2 text-white ${isWide ? "text-2xl" : "text-xl"}`}>
                      {s.title}
                    </h3>
                    <p
                      className={`leading-relaxed ${
                        isShowcase
                          ? "text-slate-300 text-sm italic"
                          : isWide
                            ? "text-slate-400 max-w-xs"
                            : "text-slate-400 text-sm"
                      }`}
                    >
                      {s.desc}
                    </p>
                    <div
                      className={`mt-8 text-[10px] tracking-[0.2em] font-bold uppercase ${
                        isShowcase ? "text-emerald-400 text-right" : "text-emerald-500"
                      } ${isWide ? "inline-block" : "block"}`}
                    >
                      {s.badge}
                    </div>
                  </div>
                  {isWide && (
                    <div className="absolute right-[-10%] top-0 w-1/2 h-full bg-gradient-to-l from-emerald-500/5 to-transparent skew-x-12 group-hover:from-emerald-500/10 transition-all" />
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-10 bg-emerald-950/20 border border-emerald-500/20 rounded-full py-4 px-8 flex items-center justify-center gap-3">
            <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse [box-shadow:0_0_8px_#10B981]" />
            <p className="text-sm text-slate-300">
              Based on typical fleet data — reclaim{" "}
              <strong className="text-emerald-400 font-bold text-lg mx-1">4.5 hours</strong>
              of core business time, daily.
            </p>
          </div>
        </div>
      </section>

      {/* --- Section 4: EAloom Evolution (E → A) --- */}
      <section id="ai" className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-14 text-center">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-4">
              EAloom{" "}
              <span className="bg-gradient-to-r from-emerald-400 to-cyan-500 bg-clip-text text-transparent">
                Evolution
              </span>
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto">
              From digital connectivity to autonomous intelligence — the roadmap to logistics efficiency.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* E — Essential Basis (Today) */}
            <div className="group relative bg-[#111] border border-white/10 rounded-2xl p-8 hover:border-white/20 transition-all">
              <div className="flex justify-between items-start mb-12">
                <div>
                  <div className="text-5xl font-mono font-bold text-slate-700 group-hover:text-slate-500 transition-colors">E</div>
                  <p className="text-sm uppercase tracking-widest text-emerald-500/80 font-semibold mt-2">
                    Essential Basis · Available Today
                  </p>
                </div>
                <Database className="w-6 h-6 text-slate-500" />
              </div>

              <div className="bg-[#0d0d0d] rounded-lg p-4 mb-8 border border-white/5">
                <div className="space-y-3">
                  {[85, 40, 65].map((w, i) => (
                    <div key={i} className="h-2 bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full bg-slate-600" style={{ width: `${w}%` }} />
                    </div>
                  ))}
                  <div className="flex justify-between text-[10px] font-mono text-slate-500 mt-2">
                    <span>OICT_SYNC: OK</span>
                    <span>INGEST_PIPE: LIVE</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-end gap-3">
                  <span className="text-3xl font-bold text-white">100%</span>
                  <span className="text-slate-400 pb-1">Email &amp; Fleet Connectivity</span>
                </div>
                <div className="flex items-end gap-3 border-t border-white/5 pt-4">
                  <span className="text-3xl font-bold text-white">24/7</span>
                  <span className="text-slate-400 pb-1">Real-time Container Tracking</span>
                </div>
              </div>
            </div>

            {/* A — Autonomous Core (Next) */}
            <div className="group relative bg-[#111] border border-emerald-500/30 rounded-2xl p-8 shadow-[0_0_30px_rgba(16,185,129,0.05)] overflow-hidden">
              <div className="absolute -top-24 -right-24 w-48 h-48 bg-emerald-500/10 blur-[100px]" />

              <div className="flex justify-between items-start mb-12">
                <div>
                  <div className="text-5xl font-mono font-bold text-emerald-500/40 group-hover:text-emerald-500 transition-colors">A</div>
                  <p className="text-sm uppercase tracking-widest text-emerald-400 font-semibold mt-2">
                    Autonomous Core · Coming Next
                  </p>
                </div>
                <Zap className="w-6 h-6 text-emerald-400 animate-pulse" />
              </div>

              <div className="bg-[#0d0d0d] rounded-lg p-4 mb-8 border border-emerald-500/20 relative">
                <div className="flex items-center justify-center h-16">
                  <Activity className="text-emerald-400/50 w-full h-full stroke-[1px]" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-20 h-20 bg-emerald-500/5 rounded-full animate-ping" />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-end gap-3 text-emerald-400">
                  <span className="text-3xl font-bold">Self-Dispatch</span>
                </div>
                <p className="text-emerald-500/60 text-sm italic -mt-2">
                  Predictive lane &amp; driver matching
                </p>
                <div className="flex items-end gap-3 border-t border-emerald-900/30 pt-4 text-emerald-400">
                  <span className="text-3xl font-bold">Self-Heal</span>
                </div>
                <p className="text-emerald-500/60 text-sm italic -mt-2">
                  Auto-reroute on port disruptions
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- Section 5: Final CTA --- */}
      <section id="demo" className="py-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-black tracking-tighter text-white mb-6 leading-[1.1]">
            Don't let legacy systems<br />
            <span className="text-emerald-400">slow your fleet down.</span>
          </h2>
          <p className="text-slate-400 mb-10 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
            Join the digital evolution — intelligent dispatch and auto-billing, the rhythm your operation deserves.
          </p>
          <button
            onClick={() => setIsDemoOpen(true)}
            className="px-10 py-4 bg-emerald-500 hover:bg-emerald-400 text-black font-black text-sm uppercase tracking-[0.25em] rounded-xl transition-all active:scale-95 shadow-[0_20px_50px_-20px_rgba(16,185,129,0.5)]"
          >
            Book Your Demo
          </button>
        </div>
      </section>

      {/* --- Footer --- */}
      <footer className="py-12 px-6 border-t border-white/5 text-center">
        <div className="flex items-center justify-center gap-6 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400 mb-5">
          <button
            type="button"
            onClick={() => navigate("/support")}
            className="hover:text-emerald-400 transition-colors"
          >
            Support
          </button>
          <button
            type="button"
            onClick={() => navigate("/privacy")}
            className="hover:text-emerald-400 transition-colors"
          >
            Privacy
          </button>
          <button
            type="button"
            onClick={() => navigate("/account-deletion")}
            className="hover:text-emerald-400 transition-colors"
          >
            Delete Account
          </button>
        </div>
        <div className="text-xs text-slate-500 font-medium leading-relaxed">
          © 2026 EAloom. All rights reserved.
        </div>
        <div className="text-xs text-slate-600 mt-1 leading-relaxed">
          EAloom is owned and operated by Lambda Flow LLC.
        </div>
      </footer>

      {/* --- Demo Request Modal --- */}
      {isDemoOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={closeDemo}
          role="dialog"
          aria-modal="true"
          aria-labelledby="demo-modal-title"
        >
          <div
            className="bg-[#0d0d0d] border border-white/10 rounded-2xl max-w-md w-full p-8 relative shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeDemo}
              aria-label="Close"
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/5 rounded-lg text-2xl leading-none transition-colors"
            >
              ×
            </button>

            {demoSubmitted ? (
              <div className="text-center py-6">
                <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-emerald-400" strokeWidth={1.8} />
                </div>
                <h3 id="demo-modal-title" className="text-2xl font-bold text-white mb-2">Request received</h3>
                <p className="text-slate-400 text-sm">Our team will reach out within 24 hours.</p>
                <button
                  onClick={closeDemo}
                  className="mt-8 px-6 py-2.5 bg-white/5 border border-white/10 hover:border-white/20 text-slate-300 text-xs uppercase tracking-[0.2em] font-bold rounded-lg transition-all"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <h3 id="demo-modal-title" className="text-2xl font-bold text-white mb-2">Begin your digital weave</h3>
                <p className="text-slate-400 text-sm mb-6">
                  Leave your details and our team will reach out within 24 hours.
                </p>

                <form onSubmit={handleDemoSubmit} className="space-y-4">
                  <input
                    type="text"
                    name="name"
                    required
                    maxLength={120}
                    disabled={demoSubmitting}
                    placeholder="Your name"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-500 focus:bg-white/[0.07] transition-colors disabled:opacity-60"
                  />
                  <input
                    type="email"
                    name="email"
                    required
                    maxLength={254}
                    disabled={demoSubmitting}
                    placeholder="Work email"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-500 focus:bg-white/[0.07] transition-colors disabled:opacity-60"
                  />

                  {demoError && (
                    <p role="alert" className="text-rose-400 text-xs leading-relaxed">
                      {demoError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={demoSubmitting}
                    className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-black py-3 rounded-lg text-sm uppercase tracking-[0.25em] transition-all active:scale-[0.98] mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {demoSubmitting ? "Sending…" : "Submit Request"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;